// Chat mode's pure rules (M2-06), kept out of the components so they are unit-tested: which
// channels a person has joined, the sidebar's grouping by space, who wrote a message and how
// it is badged (people, digital employees, crews and "via agent" posts; agents-in-chat
// research rec. 1), reactions as pills, the 15-minute edit window, a topic's roots and
// replies, card references, run cost, and which runs are working in a topic right now.
import type {
  AgentRoomView,
  Channel,
  ChannelPrefs,
  MessageView,
  Run,
  SidebarChannel,
  Space,
  TopicSummary,
} from "./chat-queries";

/** 06 §Messages: edits close 15 minutes after posting (the server says so too). */
export const EDIT_WINDOW_MS = 15 * 60_000;

export type ChatMember = {
  id: string;
  name: string;
  kind: "person" | "de";
  /**
   * WF-FIX (2026-10-26): a DE's status — the "Add digital employee" pickers leave out a retired
   * one and show a paused one disabled (only an active membership joins a channel).
   */
  deStatus?: "onboarding" | "active" | "paused" | "retired" | "not_deployed";
  /** The DE's seat ("Recruiting coordinator"), shown in mention menus and cards. */
  seat?: string | undefined;
  seatId?: string | undefined;
  /** HELPERS: a DE's optional person name (shown before its role when the company says so). */
  personName?: string | null | undefined;
  /** CHAT-DM: a person's company role, as the profile popover shows it ("Admin", "Guest"). */
  role?: string | undefined;
  /** CHAT-DM: a person who is paused or deactivated (no DM opens with them). */
  inactive?: boolean | undefined;
};

/** CHAT-DM: a person's role label from their company roles (the highest one). */
export function roleLabelOf(roles: readonly string[]): string | undefined {
  if (roles.includes("owner")) return "Owner";
  if (roles.includes("admin")) return "Admin";
  if (roles.includes("member")) return "Member";
  if (roles.includes("client_viewer")) return "Guest";
  return undefined;
}

/**
 * CHAT-DM: who a DM can be opened with — every other member of the company, people and DEs,
 * without any space or channel in common; not the viewer, a paused or deactivated person, or a
 * paused or retired DE (the API opens DMs with active memberships only). People first, then DEs, each by name; `query` filters by name, seat or role.
 */
export function dmCandidates(members: MemberIndex, meId: string | null, query = ""): ChatMember[] {
  const q = query.trim().toLowerCase();
  return [...members.values()]
    .filter(
      (m) =>
        m.id !== meId &&
        !m.inactive &&
        m.deStatus !== "retired" &&
        m.deStatus !== "paused" &&
        m.deStatus !== "not_deployed",
    )
    .filter(
      (m) =>
        !q ||
        m.name.toLowerCase().includes(q) ||
        (m.seat ?? "").toLowerCase().includes(q) ||
        (m.role ?? "").toLowerCase().includes(q),
    )
    .sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === "de" ? 1 : -1));
}

export type MemberIndex = ReadonlyMap<string, ChatMember>;

export const isDm = (c: Pick<Channel, "kind">) => c.kind === "dm" || c.kind === "group_dm";
export const isAgentRoom = (c: Pick<Channel, "kind" | "is_agent_room">) =>
  c.is_agent_room || c.kind === "agent_room";

/** A DM's member ids: its key is their ids, sorted and joined (06 "DMs"; `dmKeyOf`). */
export function dmMemberIds(c: { dm_key?: string | null }): string[] {
  return c.dm_key ? c.dm_key.split(/[,:]/).filter(Boolean) : [];
}

/**
 * Channel id → its space's name, for the channels whose name another listed channel shares
 * (#operations in two spaces): pickers show "#operations · Northwind Ops" for those only.
 */
export function spaceQualifiers(
  channels: readonly { id: string; name: string; space_id?: string | null }[],
  spaces: readonly Pick<Space, "id" | "name">[],
): Map<string, string> {
  const counts = new Map<string, number>();
  for (const c of channels) counts.set(c.name, (counts.get(c.name) ?? 0) + 1);
  const names = new Map(spaces.map((s) => [s.id, s.name]));
  const out = new Map<string, string>();
  for (const c of channels)
    if ((counts.get(c.name) ?? 0) > 1) out.set(c.id, names.get(c.space_id ?? "") ?? "No space");
  return out;
}

/** "#operations", or "#operations · Northwind Ops" when the qualifier map names its space. */
export const qualifiedChannelName = (c: { id: string; name: string }, q: Map<string, string>) =>
  q.has(c.id) ? `#${c.name} · ${q.get(c.id)}` : `#${c.name}`;

/** Who is looking: the caller's membership and the company's people and DEs by membership. */
export type LabelViewer = { meId: string | null; members: MemberIndex };

/**
 * "#recruiting", or a DM's name. With the viewer, a DM is named after the other participant(s)
 * only, by their current display names (the stored name is everyone's names when it was opened:
 * "HTVA founder, Riya Kapoor"); without it, or before the names load, the stored name.
 */
export function channelLabel(
  c: Pick<Channel, "kind" | "name"> & { dm_key?: string | null },
  viewer?: LabelViewer,
): string {
  if (!isDm(c)) return `#${c.name}`;
  const stored = c.name.trim();
  if (viewer) {
    const others = dmMemberIds(c).filter((id) => id !== viewer.meId);
    const names = others.map((id) => viewer.members.get(id)?.name);
    if (others.length && names.every(Boolean)) return names.join(", ");
    // The names are not loaded yet: the stored name without the viewer's own.
    const mine = viewer.meId ? viewer.members.get(viewer.meId)?.name : undefined;
    if (mine && stored.includes(", ")) {
      const rest = stored.split(", ").filter((n) => n !== mine);
      if (rest.length) return rest.join(", ");
    }
  }
  return stored || "Direct message";
}

/**
 * Joined channels: the caller's settings say so; without settings (none saved yet, or the
 * route not served) every visible channel counts as joined, so nobody loses a channel.
 */
export function joinedOf(
  channels: readonly SidebarChannel[],
  prefs: readonly ChannelPrefs[] | undefined,
): Set<string> {
  if (!prefs) return new Set(channels.map((c) => c.id));
  const byId = new Map(prefs.map((p) => [p.channel_id, p]));
  return new Set(
    channels.filter((c) => isDm(c) || byId.get(c.id)?.joined === true).map((c) => c.id),
  );
}

export type SpaceGroup = { space: Space; channels: SidebarChannel[] };

/** Default channels first (`#general` ahead of `#announcements`), then by name. */
function byChannelOrder(a: SidebarChannel, b: SidebarChannel): number {
  if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
  if (a.is_default && (a.name === "general") !== (b.name === "general"))
    return a.name === "general" ? -1 : 1;
  return a.name.localeCompare(b.name);
}

const byActivity = (a: SidebarChannel, b: SidebarChannel) =>
  (b.last_message_at ?? "").localeCompare(a.last_message_at ?? "");

const dmActiveAt = (c: SidebarChannel) => c.last_message_at ?? c.created_at ?? "";
const byDmActivity = (a: SidebarChannel, b: SidebarChannel) =>
  dmActiveAt(b).localeCompare(dmActiveAt(a)) || a.id.localeCompare(b.id);

/**
 * The sidebar's sections (CHAT-UX; Slack's model): Unreads (joined, unread, newest first),
 * Channels (every joined channel; grouped by space only when more than one space has one),
 * Direct messages (newest first) and, with the agents add-on, Agent rooms on their own (without
 * it a room is a channel like any other). Archived spaces and channels are left out.
 */
export type SidebarSections = {
  unreads: SidebarChannel[];
  /** Channels grouped by space; one group when `grouped` is false. */
  groups: SpaceGroup[];
  grouped: boolean;
  dms: SidebarChannel[];
  rooms: SidebarChannel[];
};

/** Grouping: `auto` groups by space only when four or more spaces hold many channels. */
export type ChannelGrouping = "auto" | "space" | "flat";
const GROUP_FROM = 16;

/**
 * A board's home channel lives with its board in Work (06: digests); it is named after the
 * board, so beside the space's channel it reads as a duplicate (`#operations` twice). Chat's
 * lists leave it out; its deep link and its unread still work.
 */
export const listedInChat = (c: Pick<Channel, "kind">) => c.kind !== "board_home";

export function sidebarSections(
  spaces: readonly Space[],
  channels: readonly SidebarChannel[],
  joined: ReadonlySet<string>,
  prefs: ReadonlyMap<string, ChannelPrefs>,
  opts: {
    agents: boolean;
    grouping?: ChannelGrouping;
    /** MULTI-SPACE-ACCESS: channel shares; grouped by space, a channel is in each space it is in. */
    shares?: readonly { object_id: string; space_id: string }[];
  },
): SidebarSections {
  const sharedInto = (c: SidebarChannel, spaceId: string) =>
    (opts.shares ?? []).some((s) => s.object_id === c.id && s.space_id === spaceId);
  // CHAT-PRESENCE: a DM the viewer closed is not listed anywhere until it opens again.
  const live = channels.filter(
    (c) => !c.archived_at && !c.closed && joined.has(c.id) && listedInChat(c),
  );
  const unreads = live
    .filter((c) => shownUnread(c, prefs.get(c.id)) > 0)
    .sort((a, b) => byActivity(a, b) || byChannelOrder(a, b));
  const rooms = opts.agents ? live.filter((c) => isAgentRoom(c) && !isDm(c)) : [];
  const roomIds = new Set(rooms.map((c) => c.id));
  const plain = live.filter((c) => !isDm(c) && !roomIds.has(c.id));
  const groups = spaces
    .filter((s) => !s.archived_at)
    .map((space) => ({
      space,
      channels: plain
        .filter((c) => c.space_id === space.id || sharedInto(c, space.id))
        .sort(byChannelOrder),
    }))
    .filter((g) => g.channels.length > 0);
  // A channel whose space is not listed (none, or one the caller cannot see) still shows.
  const placed = new Set(groups.flatMap((g) => g.channels.map((c) => c.id)));
  const loose = plain.filter((c) => !placed.has(c.id)).sort(byChannelOrder);
  if (loose.length)
    groups.push({
      space: { id: "", name: "Other", archived_at: null } as Space,
      channels: loose,
    });
  // Flat unless the person asked for spaces (Slack's shape: one light list); `auto` groups
  // only a company with many spaces and many channels.
  const grouping = opts.grouping ?? "auto";
  const grouped =
    groups.length > 1 &&
    (grouping === "space" ||
      (grouping === "auto" && groups.length >= 4 && plain.length >= GROUP_FROM));
  return {
    unreads,
    groups: grouped
      ? groups
      : groups.length
        ? [
            {
              space: groups[0]?.space as Space,
              // Flat: each channel once (a shared one sits in several groups).
              channels: [...new Map(groups.flatMap((g) => g.channels).map((c) => [c.id, c]))]
                .map(([, c]) => c)
                .sort(byChannelOrder),
            },
          ]
        : [],
    grouped,
    // CHAT-DM: newest activity first; a DM with no message yet counts from when it opened.
    dms: live.filter(isDm).sort(byDmActivity),
    rooms: rooms.sort(byChannelOrder),
  };
}

/**
 * Where Chat opens (CHAT-UX: never a landing page): the channel last visited here when it is
 * still listed, else `#general`, else the first channel in sidebar order, else the first DM;
 * null when there is nothing to open (the empty company).
 */
export function landingChannel(
  sections: SidebarSections,
  lastVisited: string | null,
): SidebarChannel | null {
  const listed = [
    ...sections.groups.flatMap((g) => g.channels),
    ...sections.rooms,
    ...sections.dms,
  ];
  const last = lastVisited ? listed.find((c) => c.id === lastVisited) : undefined;
  if (last) return last;
  return (
    listed.find((c) => c.is_default && c.name === "general" && !isDm(c)) ??
    listed.find((c) => c.name === "general" && !isDm(c)) ??
    listed[0] ??
    null
  );
}

const LAST_KEY = "companyos.chat.last";
const GROUPING_KEY = "companyos.chat.grouping";

/** The person's channel grouping in this company (`auto` until they choose). */
export function readGrouping(co: string): ChannelGrouping {
  try {
    const v = window.localStorage.getItem(`${GROUPING_KEY}.${co}`);
    return v === "space" || v === "flat" ? v : "auto";
  } catch {
    return "auto";
  }
}

export function writeGrouping(co: string, grouping: ChannelGrouping): void {
  try {
    window.localStorage.setItem(`${GROUPING_KEY}.${co}`, grouping);
  } catch {}
}

/** The channel last opened in this company, kept in the browser (nothing without storage). */
export function readLastChannel(co: string): string | null {
  try {
    return window.localStorage.getItem(`${LAST_KEY}.${co}`);
  } catch {
    return null;
  }
}

export function writeLastChannel(co: string, channelId: string): void {
  try {
    window.localStorage.setItem(`${LAST_KEY}.${co}`, channelId);
  } catch {}
}

/**
 * CHAT-TOPICS-SEPARATE: the name of a channel's own conversation (the topic messages posted to
 * the channel itself live in; a DM's one conversation too). `channels.createTopic` with it
 * returns or makes that topic.
 */
export const MAIN_TOPIC = "(no topic)";

/** CHAT-TOPICS-SEPARATE: the channel's own conversation among its topics, when it exists yet. */
export function mainTopic<T extends { is_main: boolean }>(topics: readonly T[]): T | undefined {
  return topics.find((t) => t.is_main);
}

/** CHAT-TOPICS-SEPARATE: a channel's real topics (never its own conversation). */
export function realTopics<T extends { is_main: boolean }>(topics: readonly T[]): T[] {
  return topics.filter((t) => !t.is_main);
}

type TopicOrder = Pick<
  TopicSummary,
  "pinned_at" | "archived_at" | "last_message_at" | "created_at"
>;

/**
 * A channel's topics as the sidebar and the stream list them (CHAT-UX): archived ones out
 * (unless asked for), pinned first (the latest pin on top), then by activity, newest first.
 * Closed (resolved) topics keep their place and are greyed where they show.
 */
export function orderedTopics<T extends TopicOrder>(
  topics: readonly T[],
  opts: { archived?: boolean } = {},
): T[] {
  const at = (t: T) => t.last_message_at ?? t.created_at;
  return topics
    .filter((t) => opts.archived || t.archived_at === null)
    .sort((a, b) => {
      if ((a.pinned_at === null) !== (b.pinned_at === null)) return a.pinned_at ? -1 : 1;
      if (a.pinned_at && b.pinned_at) return b.pinned_at.localeCompare(a.pinned_at);
      return at(b).localeCompare(at(a));
    });
}

/** The archived topics (the sidebar's "Archived (n)" group), the latest archived first. */
export function archivedTopics<T extends TopicOrder>(topics: readonly T[]): T[] {
  return topics
    .filter((t) => t.archived_at !== null)
    .sort((a, b) => (b.archived_at ?? "").localeCompare(a.archived_at ?? ""));
}

/** Unread to show: muted channels show none (06: "nothing else blinks"). */
export function shownUnread(c: SidebarChannel, prefs: ChannelPrefs | undefined): number {
  return prefs?.muted ? 0 : c.unread_count;
}

export type Author = {
  id: string;
  name: string;
  kind: "person" | "agent";
  /** The badge text for agents: "DE", "Maya's agent". */
  badge?: string;
  /** WF-FIX (2026-10-26): a DE author: the header shows the `WorkerBadge` (crews keep `badge`). */
  workerKind?: "de";
  /** Posted by an agent on a person's behalf ("via Maya's agent"). */
  via?: string;
  system?: boolean;
};

const firstName = (name: string) => name.split(/\s+/)[0] ?? name;

/**
 * Who wrote a message, and how it is marked (research rec. 1; 06 §Messages, agent rooms). In an
 * agent room, `badges` (the room's participant badges, M4-05) names agents by their seat or
 * owner: "DE · Recruiting coordinator", "Jordan's agent".
 */
export function authorOf(
  m: MessageView,
  members: MemberIndex,
  meId: string | null,
  badges?: ReadonlyMap<string, RoomBadge>,
): Author {
  const a = baseAuthorOf(m, members, meId);
  const b = badges?.get(m.author_membership_id ?? "");
  // A crew posting as itself is a room agent the member index does not list.
  if (!b || a.via || a.system) return a;
  return { ...a, name: members.has(a.id) ? a.name : b.name, kind: "agent", badge: b.label };
}

export type RoomBadge = { name: string; label: string };

/** The room's agents by membership, with the badge their messages carry (M4-05). */
export function roomBadges(room: AgentRoomView | null | undefined): Map<string, RoomBadge> {
  return new Map(
    (room?.participants ?? [])
      .filter((p) => p.kind === "agent")
      .map((p) => [p.membership_id, { name: p.display_name, label: p.badge.label }]),
  );
}

/**
 * The room's turn line: "Turn: Riley", "Paused after 8 agent turns — post to continue", or why
 * nobody holds it. Taken over beats both: the banner says so.
 */
export function turnLine(room: AgentRoomView): { text: string; stalled: boolean } | null {
  if (room.taken_over_by_membership_id) return null;
  if (room.room.stalled_at)
    return {
      text: `Paused after ${room.room.agent_turns_since_human} agent turns — post to continue`,
      stalled: true,
    };
  const holder = room.participants.find((p) => p.is_turn);
  if (holder) return { text: `Turn: ${holder.display_name}`, stalled: false };
  return room.participants.some((p) => p.kind === "agent")
    ? null
    : { text: "No agents yet — add one to start the turns", stalled: false };
}

export type Candidate = { id: string; name: string; hint: string; kind: "de" | "crew" | "person" };

/**
 * Who can be added to a room: the company's DEs, the caller's own crew (never someone else's),
 * and people; minus who is already in it. DEs and the crew first.
 */
export function roomCandidates(
  room: AgentRoomView | null | undefined,
  members: MemberIndex,
  meId: string | null,
  crew: { id: string; name: string } | null,
): Candidate[] {
  const inRoom = new Set(room?.participants.map((p) => p.membership_id));
  const des: Candidate[] = [];
  const people: Candidate[] = [];
  for (const m of members.values()) {
    if (inRoom.has(m.id) || m.id === meId) continue;
    if (m.kind === "de")
      des.push({ id: m.id, name: m.name, hint: `DE${m.seat ? ` · ${m.seat}` : ""}`, kind: "de" });
    else people.push({ id: m.id, name: m.name, hint: "Person", kind: "person" });
  }
  const byName = (a: Candidate, b: Candidate) => a.name.localeCompare(b.name);
  const mine: Candidate[] =
    crew && !inRoom.has(crew.id)
      ? [{ id: crew.id, name: crew.name, hint: "Your agent", kind: "crew" }]
      : [];
  return [...des.sort(byName), ...mine, ...people.sort(byName)];
}

function baseAuthorOf(m: MessageView, members: MemberIndex, meId: string | null): Author {
  const id = m.author_membership_id;
  // CHAT-SWITCH-B: an incoming webhook posts under its own name, labelled "Integration".
  if (m.integration)
    return {
      id: `hook:${m.integration.webhook_id}`,
      name: m.integration.name,
      kind: "person",
      badge: "Integration",
    };
  if (id === null && m.actor_kind === "system")
    return { id: "system", name: "CompanyOS", kind: "person", system: true };
  const who = id ? members.get(id) : undefined;
  const name = who?.name ?? (id && id === meId ? "You" : id ? "Former member" : "Agency staff");
  if (m.actor_crew_agent_id && m.actor_identity_mode !== "on_behalf_of") {
    const owner = members.get(m.actor_acting_for_membership_id ?? id ?? "")?.name ?? name;
    return {
      id: m.actor_crew_agent_id,
      name: `${firstName(owner)}'s agent`,
      kind: "agent",
      badge: "Crew",
    };
  }
  if (m.actor_identity_mode === "on_behalf_of") {
    const owner = members.get(m.actor_acting_for_membership_id ?? id ?? "")?.name ?? name;
    return { id: id ?? "unknown", name: owner, kind: "person", via: `${firstName(owner)}'s agent` };
  }
  if (who?.kind === "de" || m.actor_kind === "digital_employee")
    return { id: id ?? "unknown", name, kind: "agent", badge: "DE", workerKind: "de" };
  return { id: id ?? "unknown", name, kind: "person" };
}

export type ReactionPill = { emoji: string; count: number; reacted: boolean; names: string[] };

/** A message's reactions as pills, in first-reaction order. */
export function reactionPills(
  m: MessageView,
  members: MemberIndex,
  meId: string | null,
): ReactionPill[] {
  const pills = new Map<string, ReactionPill>();
  for (const r of m.reactions) {
    const pill = pills.get(r.emoji) ?? { emoji: r.emoji, count: 0, reacted: false, names: [] };
    pill.count += 1;
    if (r.membership_id === meId) pill.reacted = true;
    pill.names.push(
      r.membership_id === meId ? "You" : (members.get(r.membership_id)?.name ?? "Someone"),
    );
    pills.set(r.emoji, pill);
  }
  return [...pills.values()];
}

/** The author may still edit: their own, not deleted, inside the window. */
export function canEdit(m: MessageView, meId: string | null, now: Date): boolean {
  return (
    meId !== null &&
    m.author_membership_id === meId &&
    !m.deleted_at &&
    now.getTime() - new Date(m.created_at).getTime() < EDIT_WINDOW_MS
  );
}

export type ThreadSplit = {
  roots: MessageView[];
  replies: Map<string, MessageView[]>;
  /** CHAT-PLACEMENT-FIX: roots that loaded replies hang from but the loaded pages do not hold. */
  missing: string[];
};

/**
 * A topic's messages split into the main line (roots) and the replies under each root.
 * CHAT-PLACEMENT-FIX (2026-10-30): a reply is never on the main line. One whose root is not in
 * the loaded pages (a long thread pushes its root past the first page) waits under that root's
 * id, listed in `missing` so the pane can fetch the root; before, it fell onto the main line, so
 * a DE's answer in a thread read as a topic message and the person's own reply showed twice.
 */
export function threadsOf(messages: readonly MessageView[]): ThreadSplit {
  const byId = new Map(messages.map((m) => [m.id, m]));
  const roots: MessageView[] = [];
  const replies = new Map<string, MessageView[]>();
  const missing = new Set<string>();
  for (const m of messages) {
    const loaded = rootIdOf(m, byId);
    const top = byId.get(loaded) ?? m;
    // The chain stops at a parent the pages do not hold: the reply belongs to that one's root.
    const root = top.parent_message_id ?? loaded;
    if (root !== loaded) missing.add(root);
    if (root !== m.id) {
      const list = replies.get(root) ?? [];
      list.push(m);
      replies.set(root, list);
    } else roots.push(m);
  }
  return { roots, replies, missing: [...missing] };
}

/**
 * CHAT-PLACEMENT-FIX: the loaded pages plus the fetched roots their replies hang from, in time
 * order (a root found by `messages.locate` joins the line at its own time, not as new).
 */
export function withRoots<T extends Pick<MessageView, "id" | "created_at">>(
  messages: readonly T[],
  roots: readonly (T | undefined)[],
): T[] {
  const have = new Set(messages.map((m) => m.id));
  const add = new Map<string, T>();
  for (const r of roots) if (r && !have.has(r.id)) add.set(r.id, r);
  if (!add.size) return [...messages];
  return [...add.values(), ...messages].sort((a, b) => a.created_at.localeCompare(b.created_at));
}

/**
 * CHAT-AGENT-IMAGES: the loaded root a message's reply thread hangs from. Replies are one level
 * deep, but older rows (an agent answering the reply that woke it) name a reply as their parent:
 * they belong to that reply's root, where their neighbours show, not under an unseen reply.
 */
export function rootIdOf(
  m: Pick<MessageView, "id" | "parent_message_id">,
  byId: ReadonlyMap<string, Pick<MessageView, "id" | "parent_message_id">>,
): string {
  let at = m;
  const seen = new Set([m.id]);
  while (at.parent_message_id) {
    const up = byId.get(at.parent_message_id);
    if (!up || seen.has(up.id)) break;
    seen.add(up.id);
    at = up;
  }
  return at.id;
}

export type CardRef = {
  kind:
    | "task"
    | "task_link"
    | "approval"
    | "page"
    | "delegation_request"
    | "screen_ask"
    | "flow"
    | "metric";
  id: string;
};

export function cardRefs(m: Pick<MessageView, "card_refs">): CardRef[] {
  return m.card_refs.flatMap((r) => {
    const [kind, id] = r.split(":");
    return (kind === "task" ||
      // DE-REPLY-STYLE: a reply's link to its task (a small chip, `TaskEmbed` `chip`).
      kind === "task_link" ||
      kind === "approval" ||
      kind === "page" ||
      kind === "delegation_request" ||
      kind === "screen_ask" ||
      // GUIDED-FLOW: "Complete your seat setup, 3 of 5 chapters" (cards.tsx `FlowCardEmbed`).
      kind === "flow" ||
      // DE-INITIATE: a DE's unprompted post about a KPI (`MetricChip`).
      kind === "metric") &&
      id
      ? [{ kind, id }]
      : [];
  });
}

/** "$0.042", "$1.20", "<$0.001" from micro-dollars. */
export function formatCost(micros: number): string {
  if (micros <= 0) return "$0";
  const dollars = micros / 1_000_000;
  if (dollars < 0.001) return "<$0.001";
  if (dollars < 1) return `$${dollars.toFixed(3)}`;
  return `$${dollars.toFixed(2)}`;
}

/** "8.1k tokens". */
export function formatTokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

/** Runs still working (or parked) whose trigger is this topic or one of its messages. */
export function runsInTopic(
  runs: readonly Run[],
  topicId: string,
  messageIds: ReadonlySet<string>,
): Run[] {
  const live = new Set<Run["state"]>([
    "queued",
    "running",
    "waiting_input",
    "awaiting_approval",
    "waiting_children",
  ]);
  return runs.filter(
    (r) =>
      live.has(r.state) &&
      r.trigger_ref?.id != null &&
      ((r.trigger_ref.kind === "topic" && r.trigger_ref.id === topicId) ||
        (r.trigger_ref.kind === "message" && messageIds.has(r.trigger_ref.id))),
  );
}

/** What an agent is doing, for its state line ("working", "parked: …"). */
export function runStateLabel(r: Run): string {
  switch (r.state) {
    case "queued":
      return "queued";
    case "running":
      return "working";
    case "waiting_input":
      return r.state_reason?.startsWith("parked") ? "parked (taken over)" : "waiting for input";
    case "awaiting_approval":
      return "awaiting approval";
    case "waiting_children":
      return "waiting on a delegate";
    default:
      return r.state;
  }
}

/**
 * Mention debouncing as the person sees it (research rec. 4): a message that mentions an agent
 * while that agent's run in this topic is in flight joins the run's next turn, not a new run.
 */
export function queuedFor(
  m: Pick<MessageView, "mentions" | "created_at">,
  inFlight: readonly Run[],
): string[] {
  return inFlight
    .filter(
      (r) =>
        (r.state === "running" || r.state === "queued") &&
        r.started_at !== null &&
        m.created_at > r.started_at &&
        m.mentions.some((x) => x === `de:${r.membership_id}` || x === `person:${r.membership_id}`),
    )
    .map((r) => r.membership_id);
}

/** The id of the last read message, from the topic's unread count (the "New" line). */
export function lastReadId(
  roots: readonly MessageView[],
  topic: Pick<TopicSummary, "unread_count"> | undefined,
): string | undefined {
  const unread = topic?.unread_count ?? 0;
  if (unread <= 0 || unread >= roots.length) return undefined;
  return roots[roots.length - unread - 1]?.id;
}

/** "3:05" from seconds. */
export function elapsed(fromIso: string, now: Date): string {
  const s = Math.max(0, Math.round((now.getTime() - new Date(fromIso).getTime()) / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

// CHAT-DE: talking to digital employees as simply as to a Slack app --------------------------

/** The DEs a person talks to in Chat (the sidebar's section): not retired, not in setup. */
export function talkableDes(members: MemberIndex): ChatMember[] {
  return [...members.values()]
    .filter((m) => m.kind === "de" && m.deStatus !== "retired" && m.deStatus !== "onboarding")
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The viewer's one-to-one DMs with a DE, by the DE's membership id. They sit on the DE's row in
 * the sidebar's "Digital employees" (Slack's app DM), not under Direct messages; group DMs stay.
 */
export function deDms(
  dms: readonly SidebarChannel[],
  members: MemberIndex,
  meId: string | null,
): Map<string, SidebarChannel> {
  const out = new Map<string, SidebarChannel>();
  if (meId === null) return out;
  for (const c of dms) {
    if (c.kind !== "dm") continue;
    const ids = dmMemberIds(c);
    const other = ids.find((id) => id !== meId);
    if (ids.length === 2 && ids.includes(meId) && other && members.get(other)?.kind === "de")
      out.set(other, c);
  }
  return out;
}

/** The DE membership ids a composer's mention refs name (`de:` or `person:` of a DE). */
export function mentionedDeIds(refs: readonly string[], members: MemberIndex): string[] {
  const out: string[] = [];
  for (const ref of refs) {
    const [kind, id] = ref.split(":");
    if ((kind === "de" || kind === "person") && id && members.get(id)?.kind === "de")
      if (!out.includes(id)) out.push(id);
  }
  return out;
}

/** How long an unanswered ask still shows "working…" (a lost reply never spins forever). */
export const WORKING_MAX_MS = 10 * 60_000;

/**
 * DE-QUICK-ACK: an agent's instant first reply ("On it — …", or a quick question), posted under
 * its `qr:` client id before its main run. It is not the answer: the "is working…" line stays.
 * QUICK-REPLY-NATURAL: a "done" quick reply (`qr:…:done`) IS the answer, so it ends the line.
 */
export function isQuickReply(m: Partial<Pick<MessageView, "client_id" | "author_kind">>): boolean {
  return (
    m.author_kind === "agent" &&
    typeof m.client_id === "string" &&
    m.client_id.startsWith("qr:") &&
    !m.client_id.endsWith(":done")
  );
}

/**
 * The agents at work on an ask in these messages (the "is working…" line): a wake-up still
 * queued or running, asked less than `WORKING_MAX_MS` ago, with no message from that agent
 * since (its quick first reply does not count). Its reply landing, or its run ending, ends the
 * line. `running` (DE-QUICK-ACK): a bridge or the managed runtime picked the ask up (its run is
 * checked out); before that it is still waiting to be picked up.
 */
export function workingAgents(
  messages: readonly (Pick<
    MessageView,
    "id" | "created_at" | "author_membership_id" | "agent_wakes" | "deleted_at"
  > &
    Partial<Pick<MessageView, "client_id" | "author_kind">>)[],
  now: Date,
): { id: string; name: string; running: boolean }[] {
  const out = new Map<string, { id: string; name: string; since: string; running: boolean }>();
  for (const m of messages) {
    if (m.deleted_at) continue;
    for (const w of m.agent_wakes ?? []) {
      if (w.state !== "queued" && w.state !== "running") continue;
      const since = w.requested_at ?? m.created_at;
      if (now.getTime() - new Date(since).getTime() > WORKING_MAX_MS) continue;
      const prior = out.get(w.membership_id);
      const running = w.state === "running" || (prior?.running ?? false);
      if (!prior || prior.since < since)
        out.set(w.membership_id, { id: w.membership_id, name: w.name, since, running });
      else prior.running = running;
    }
  }
  return [...out.values()]
    .filter(
      (a) =>
        !messages.some(
          (m) =>
            m.author_membership_id === a.id &&
            m.created_at > a.since &&
            !m.deleted_at &&
            !isQuickReply(m),
        ),
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(({ id, name, running }) => ({ id, name, running }));
}

/**
 * CHAT-THREAD-AGENTS: where an agent's answer to `m` lands — the root of its reply thread, or
 * null for the main line. As the API answers (mcp/run.ts `replyTargets`): a reply's root; in a
 * channel, a top-level message's own thread (Slack: answer in its thread); a DM's top level.
 */
export function answerRootOf(
  m: Pick<MessageView, "id" | "parent_message_id">,
  dm: boolean,
): string | null {
  return m.parent_message_id ?? (dm ? null : m.id);
}

type Workable = Pick<
  MessageView,
  "id" | "created_at" | "author_membership_id" | "agent_wakes" | "deleted_at" | "parent_message_id"
> &
  Partial<Pick<MessageView, "client_id" | "author_kind">>;

/** CHAT-THREAD-AGENTS: the messages whose agents answer on the main line (not in a thread). */
export function mainLineAsks<T extends Workable>(messages: readonly T[], dm: boolean): T[] {
  return messages.filter((m) => answerRootOf(m, dm) === null);
}

/** CHAT-THREAD-AGENTS: the messages of one reply thread whose agents answer in it. */
export function threadAsks<T extends Workable>(
  messages: readonly T[],
  root: string,
  dm: boolean,
): T[] {
  return messages.filter((m) => answerRootOf(m, dm) === root);
}

/**
 * CHAT-THREAD-AGENTS: the agents answering in each reply thread, by root ("Riley is replying…"
 * on the root's reply line): working on an ask whose answer lands there (`workingAgents` over
 * that thread's messages), or with a run in flight triggered by one of them.
 */
export function replyingByRoot(
  messages: readonly Workable[],
  dm: boolean,
  now: Date,
  runs: readonly Pick<Run, "membership_id" | "trigger_ref">[] = [],
  nameOf: (id: string) => string = () => "An agent",
): Map<string, string[]> {
  const groups = new Map<string, Workable[]>();
  const rootOf = new Map<string, string>();
  for (const m of messages) {
    const root = answerRootOf(m, dm);
    if (root === null) continue;
    rootOf.set(m.id, root);
    groups.set(root, [...(groups.get(root) ?? []), m]);
  }
  const out = new Map<string, string[]>();
  for (const [root, list] of groups) {
    const names = workingAgents(list, now).map((a) => a.name);
    if (names.length) out.set(root, names);
  }
  for (const r of runs) {
    const id = r.trigger_ref?.kind === "message" ? r.trigger_ref.id : null;
    const root = id ? rootOf.get(id) : undefined;
    if (!root) continue;
    const name = nameOf(r.membership_id);
    const names = out.get(root) ?? [];
    if (!names.includes(name))
      out.set(
        root,
        [...names, name].sort((a, b) => a.localeCompare(b)),
      );
  }
  return out;
}

/** CHAT-THREAD-AGENTS: the runs answering on the main line (the rest show in their thread). */
export function mainLineRuns<R extends Pick<Run, "trigger_ref">>(
  runs: readonly R[],
  messages: readonly Pick<MessageView, "id" | "parent_message_id">[],
  dm: boolean,
): R[] {
  const threaded = new Set(messages.filter((m) => answerRootOf(m, dm) !== null).map((m) => m.id));
  return runs.filter(
    (r) =>
      !(r.trigger_ref?.kind === "message" && r.trigger_ref.id && threaded.has(r.trigger_ref.id)),
  );
}

/** CHAT-THREAD-AGENTS: the runs answering in one reply thread. */
export function threadRuns<R extends Pick<Run, "trigger_ref">>(
  runs: readonly R[],
  messages: readonly Pick<MessageView, "id" | "parent_message_id">[],
  root: string,
  dm: boolean,
): R[] {
  const mine = new Set(messages.filter((m) => answerRootOf(m, dm) === root).map((m) => m.id));
  return runs.filter(
    (r) => r.trigger_ref?.kind === "message" && !!r.trigger_ref.id && mine.has(r.trigger_ref.id),
  );
}

/** Interleaved view: every loaded topic's messages in time order, each with its topic. */
export function interleave<T extends { created_at: string }>(
  byTopic: readonly { topicId: string; messages: readonly T[] }[],
): (T & { topicId: string })[] {
  return byTopic
    .flatMap((t) => t.messages.map((m) => ({ ...m, topicId: t.topicId })))
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
}
